import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ArrowDownOnSquareIcon,
  ArrowLeftEndOnRectangleIcon,
  ChevronDownIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { isDemoStore, isTestStore, type Store } from '../../data/store';
import { formatShortDate } from '../../helpers/format-date';
import { useManageBilling } from '../../hooks/manage-billing';
import { useLogout } from '../../hooks/use-logout';
import { useStoreSwitch } from '../../hooks/use-store-switch';
import { storesQueryOptions } from '../../query/query-options';
import { DemoCTAModal, useDemoCTA } from '../demo-cta-modal';
import { uploaderUrl } from '../download-uploader-button';
import { Spinner } from '../spinner';

const menuItemClassName =
  'ui-active:bg-gray-100 inline-flex w-full justify-left px-4 py-2 text-sm font-medium text-gray-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/75 gap-3 sm:whitespace-nowrap';

// menu item that has an onClick function rather than a link
const StoreMenuAction: React.FC<{
  children: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ onClick, children }) => {
  return (
    <MenuItem as="button" className={menuItemClassName} onClick={onClick}>
      {children}
    </MenuItem>
  );
};

export const StoreMenu: React.FC<{ store: Store }> = ({ store: selectedStore }) => {
  // enabled:false — read-only subscriber; never triggers a fetch. Unlike skipToken
  // (truthy Symbol), undefined queryFn lets TanStack's fallback observer scan work.
  const { data: stores = [] } = useQuery({ ...storesQueryOptions(), enabled: false });
  const navigate = useNavigate();
  const switchStore = useStoreSwitch();
  const logout = useLogout();
  const { showBilling, loadingBilling } = useManageBilling();
  const { isDemo, showCTA, ctaProps } = useDemoCTA(selectedStore);

  const otherStores = useMemo(() => {
    return stores.filter(
      (store) => store.id !== selectedStore.id && !isTestStore(store) && !isDemoStore(store)
    );
  }, [stores, selectedStore]);

  const demoStores = useMemo(() => {
    return stores.filter((store) => isDemoStore(store) && store.id !== selectedStore.id);
  }, [stores, selectedStore]);

  const testStores = useMemo(() => {
    return stores.filter((store) => isTestStore(store));
  }, [stores]);

  return (
    <>
      <DemoCTAModal {...ctaProps} />
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          className={
            isDemo
              ? 'rounded-md border border-primary-600 px-3 py-2 text-sm font-medium text-primary-100 hover:bg-primary-800 hover:text-white data-[open]:bg-primary-800 focus-visible:outline-none flex flex-row justify-left items-center gap-1'
              : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-primary-700 hover:text-white focus-visible:outline-none flex flex-row justify-left items-center gap-1'
          }
          disabled={loadingBilling}
        >
          {!selectedStore.enabled && (
            <ExclamationCircleIcon
              className="h-4 w-4 text-orange-400"
              aria-label="Store disabled"
            />
          )}
          {selectedStore.name}
          {!loadingBilling && <ChevronDownIcon className="-mr-2 h-5 w-5" aria-hidden="true" />}
          {loadingBilling && <Spinner />}
        </MenuButton>
        <MenuItems className="absolute right-0 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden z-50 max-sm:max-w-[calc(100vw-2rem)] flex flex-col max-h-[calc(100vh-5rem)]">
          <div className="py-1 divide-y divide-gray-100 flex flex-col flex-1 min-h-0">
            {(otherStores.length > 0 || demoStores.length > 0 || testStores.length > 0) && (
              <div className="overflow-y-auto flex-1 min-h-0 divide-y divide-gray-100">
                {otherStores.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-400 p-2">Change Store</span>
                    {otherStores.map((store) => (
                      <StoreMenuAction
                        key={store.id}
                        onClick={() => {
                          switchStore(store);
                        }}
                      >
                        {!store.enabled && (
                          <ExclamationCircleIcon
                            className="h-4 w-4 text-orange-400"
                            aria-label="Store disabled"
                          />
                        )}
                        {store.name}
                      </StoreMenuAction>
                    ))}
                  </div>
                )}
                {demoStores.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-400 p-2">Demo Stores</span>
                    {demoStores.map((store) => (
                      <StoreMenuAction
                        key={store.id}
                        onClick={() => {
                          switchStore(store);
                        }}
                      >
                        {store.name}
                      </StoreMenuAction>
                    ))}
                  </div>
                )}
                {testStores.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-400 p-2">Test Stores</span>
                    {testStores.map((store) => (
                      <StoreMenuAction
                        key={store.id}
                        onClick={() => {
                          switchStore(store);
                        }}
                      >
                        {store.name}
                      </StoreMenuAction>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="shrink-0">
              {selectedStore.subscription_id && (
                <StoreMenuAction onClick={isDemo ? showCTA : showBilling}>
                  <CreditCardIcon className="h-5 w-5" aria-hidden="true" />
                  Manage Billing
                </StoreMenuAction>
              )}
              <StoreMenuAction
                onClick={() =>
                  navigate({
                    to: '/stores/$storeId/edit',
                    params: { storeId: selectedStore.id },
                  })
                }
              >
                <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
                Manage Store
              </StoreMenuAction>
              <StoreMenuAction
                onClick={() =>
                  navigate({
                    to: '/stores/$storeId/ai-assistant',
                    params: { storeId: selectedStore.id },
                  })
                }
              >
                <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                AI Assistant
              </StoreMenuAction>
              <StoreMenuAction
                onClick={() => navigate({ to: '/onboard', search: { checkout: undefined } })}
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                Add a Store
              </StoreMenuAction>
              <hr />
              <StoreMenuAction
                onClick={() => {
                  window.open(uploaderUrl, '_blank');
                }}
              >
                <ArrowDownOnSquareIcon className="h-5 w-5" aria-hidden="true" />
                Download Companion
              </StoreMenuAction>
              <hr />
              <StoreMenuAction onClick={logout}>
                <ArrowLeftEndOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                Logout
              </StoreMenuAction>
            </div>
            <div className="shrink-0 px-4 py-2 text-xs text-gray-400">
              Version {import.meta.env.VITE_APP_VERSION} released on{' '}
              {formatShortDate(new Date(import.meta.env.VITE_BUILD_DATE))}
            </div>
          </div>
        </MenuItems>
      </Menu>
    </>
  );
};
